import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import {
  catchError,
  delay,
  map,
  mergeMap,
  switchMap,
  takeWhile,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import { OnboardingAPI } from '../../../data/mo-api/onboarding.api';

import { APP_CONSTANTS as CONST } from '@app/app.constants';
import { Store } from '@ngrx/store';
import { interval, of } from 'rxjs';
import { EAUTH10TIX_STATUS, EONBOARDING_STEPS } from '../../../core/enums/onboarding.enum';
import { IOnboardingKYC, IOnboardingSession } from '../../../core/models/onboarding.model';
import { HandleTokenService } from '../../../core/services/handle-token.service';
import { AppFacade } from '../../../facades/app.facade';
import { ErrorFacade } from '../../../facades/error.facade';
import { OnboardingFacade } from '../../../facades/onboarding.facade';
import { MapErrorService } from '../../services/error/map-error.service';

import * as appSelectors from '@store/app/app.selectors';
import * as onboardingActions from '@store/onboarding/onboarding.actions';
import { IOnboardingParamsWithoutFormat } from '../../../core/models/onboarding.model';
import { OnboardingService } from '../../services/onboarding/onboarding.service';
import { CustomerAPI } from '../../../data/mo-api/customer.api';
import { UtilService } from '@app/services/util.service';
import { EVENTS } from '@app/data/events/events-by-category';

@Injectable()
export class OnboardingEffects {
  public AUTH10TIX_STEP = EONBOARDING_STEPS.AUTH10TIX;
  public AUTH10TIX_STATUS = EAUTH10TIX_STATUS;

  constructor(
    private actions$: Actions,
    private _onboardingAPI: OnboardingAPI,
    private _onboardingFacade: OnboardingFacade,
    private _onboardingService: OnboardingService,
    private _handleTokenService: HandleTokenService,
    private _customerAPI: CustomerAPI,
    private _appFacade: AppFacade,
    private _errorFacade: ErrorFacade,
    private _mapErrorService: MapErrorService,
    private _utilService: UtilService,
    private _store: Store
  ) {}

  public requestOnboardingSession$ = createEffect(() =>
    this.actions$.pipe(
      ofType(onboardingActions.requestOnboardingSession),
      switchMap(({ step }) =>
        this._onboardingAPI.requestOnboardingSession(step).pipe(
          map((information: IOnboardingSession) =>
            onboardingActions.successRequestOnboardingSession({ information })
          ),
          catchError((error) =>
            of(
              onboardingActions.failureRequestOnboardingSession({
                message: error,
              })
            )
          )
        )
      )
    )
  );

  public failureRequestOnboardingSession$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(onboardingActions.failureRequestOnboardingSession),
        tap(({ message }) => {
          const messageError = this._mapErrorService.mapError(message);
          this._errorFacade.handleError(messageError);
        })
      ),
    { dispatch: false }
  );

  public saveOnboardingStep$ = createEffect(() =>
    this.actions$.pipe(
      ofType(onboardingActions.saveOnboardingStep),
      switchMap(({ stepInfo }) =>
        this._onboardingAPI.savePreCustomer(stepInfo).pipe(
          map((_) => {
            this._appFacade.nextStep();
            return onboardingActions.successSaveOnboardingStep();
          }),
          catchError((error) => of(onboardingActions.failureSaveOnboardingStep({ message: error, formData: stepInfo })))
        )
      )
    )
  );

  public successSaveOnboardingStep$ = createEffect(() =>
    this.actions$.pipe(
      ofType(onboardingActions.successSaveOnboardingStep),
      withLatestFrom(this._store.select(appSelectors.selectTransactions)),
      switchMap(([_, { id }]) =>
        this._customerAPI.requestCustomerValidation(id).pipe(
          map((_) => onboardingActions.successUpgradeCustomer()),
          catchError((error) => of(onboardingActions.failureUpgradeCustomer({ message: error })))
        )
      )
    )
  );

  public failureUpgradeCustomer$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(onboardingActions.failureUpgradeCustomer),
        tap(({ message }) => {
          const messageError = this._mapErrorService.mapError(message);
          this._errorFacade.handleError(messageError);
        })
      ),
    { dispatch: false }
  );

  public successUpgradeCustomer$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(onboardingActions.successUpgradeCustomer),
        tap((_) => {
          const event = EVENTS.KYC.VISIT;
          const dataFingerPrint = this._utilService.getFingerPrintData();
          this._appFacade.sendPutRecord(event);
          this._appFacade.setDataFingerPint(dataFingerPrint);
        })
      ),
    { dispatch: false }
  );

  public failureSaveOnboardingStep$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(onboardingActions.failureSaveOnboardingStep),
        tap(({ message }) => {
          const messageError = this._mapErrorService.mapError(message);
          this._errorFacade.handleError(messageError);
        })
      ),
    { dispatch: false }
  );

  public saveAuth10tixStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(onboardingActions.saveAuth10tixStatus),
      withLatestFrom(this._store.select(appSelectors.selectStep)),
      switchMap(([_, step]) =>
        this._onboardingAPI.saveOnboardingStep(step, {}).pipe(
          map((_) => onboardingActions.successSaveAuth10tixStatus()),
          catchError((error) =>
            of(onboardingActions.failureSaveAuth10tixStatus({ message: error }))
          )
        )
      )
    )
  );

  public failureSaveAuth10tixStatus$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(onboardingActions.failureSaveAuth10tixStatus),
        tap(({ message }) => {
          const messageError = this._mapErrorService.mapError(message);
          this._errorFacade.handleError(messageError);
        })
      ),
    { dispatch: false }
  );

  public requestAuth10tixStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(onboardingActions.requestAuth10tixStatus),
      switchMap((_) =>
        this._onboardingAPI.requestOnboardingSession(this.AUTH10TIX_STEP).pipe(
          map((information: IOnboardingSession) => {
            const { data } = information;
            return onboardingActions.successRequestAuth10tixStatus({
              auth10tix: data,
            });
          }),
          catchError((error) =>
            of(
              onboardingActions.failureRequestAuth10tixStatus({
                message: error,
              })
            )
          )
        )
      )
    )
  );

  public failureRequestAuth10tixStatus$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(onboardingActions.failureRequestAuth10tixStatus),
        tap(({ message }) => {
          const messageError = this._mapErrorService.mapError(message);
          this._errorFacade.handleError(messageError);
        })
      ),
    { dispatch: false }
  );

  public listentAuth10tixStatus$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(onboardingActions.listentAuth10tixStatus),
        delay(3000),
        tap((_) => {
          return interval(CONST.LISTEN_KYC_STATUS)
            .pipe(
              mergeMap(() => this._onboardingAPI.requestKYCStatus(this.AUTH10TIX_STEP)),
              tap((auth10tix: IOnboardingKYC) => {
                const { status } = auth10tix;
                return status !== this.AUTH10TIX_STATUS.UNDER_REVIEW
                  ? this._onboardingFacade.successAuth10tixStatuss(auth10tix)
                  : false;
              }),
              takeWhile(({ status }) => status === this.AUTH10TIX_STATUS.UNDER_REVIEW)
            )
            .subscribe();
        })
      ),
    { dispatch: false }
  );

  public successAuth10tixStatuss$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(onboardingActions.successRequestAuth10tixStatus),
        tap((_) => {
          this._appFacade.setIsLoading();
        })
      ),
    { dispatch: false }
  );

  public getPreCustomer$ = createEffect(() =>
    this.actions$.pipe(
      ofType(onboardingActions.getPreCustomer),
      switchMap((_) =>
        this._onboardingAPI.getPreCustomer().pipe(
          map((stepInfo: IOnboardingParamsWithoutFormat) =>
            onboardingActions.successGetPreCustomer({
              stepInfo: this._onboardingService.buildCustomerInfo(stepInfo),
            })
          ),
          catchError((error) => of(onboardingActions.failureGetPreCustomer({ message: error })))
        )
      )
    )
  );

  // DEMO
  public setUnderReviewAuth10tixStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(onboardingActions.setUnderReviewAuth10tixStatus),
      delay(10000),
      map((_) =>
        onboardingActions.setApprovedAuth10tixStatus({
          status: this.AUTH10TIX_STATUS.APPROVED,
        })
      )
    )
  );
  // DEMO
}
